import type { ReactNode } from 'react'
import { Box, Container, Typography } from '@mui/material'
import Logo from './Logo'
import { resolveAssetUrl } from '../services/assetUrl'

export interface EventBrand {
  name?: string | null
  tagline?: string | null
  primaryColor?: string | null
  accentColor?: string | null
  logoUrl?: string | null
  loginBackgroundUrl?: string | null
  heroImage?: string | null
}

const DEFAULT_GRADIENT =
  'linear-gradient(150deg, #0075BC 0%, #1C5580 55%, #14384f 100%)'

/** The brand-panel background: login background image → hero image → gradient
 *  built from the event's colors (or the default Vericast gradient). */
function brandBackground(brand: EventBrand): string {
  const img = brand.loginBackgroundUrl || brand.heroImage
  if (img) {
    // Darken the image so overlaid white text stays legible.
    return `linear-gradient(rgba(20,40,60,0.55), rgba(20,40,60,0.55)), url(${resolveAssetUrl(
      img,
    )})`
  }
  const p = brand.primaryColor
  const a = brand.accentColor
  if (p && a) return `linear-gradient(150deg, ${p} 0%, ${a} 100%)`
  if (p) return `linear-gradient(150deg, ${p} 0%, #14384f 100%)`
  return DEFAULT_GRADIENT
}

/**
 * The branded two-panel shell shared by the per-event sign-in page, the public
 * registration page, and the admin login-page preview: a brand hero panel
 * (event background/logo/name/tagline) beside a content panel (children).
 *
 * ``compact`` renders a smaller, non-sticky variant used inside the admin
 * preview so it fits within the editor.
 */
export default function EventBrandShell({
  brand,
  children,
  compact = false,
}: {
  brand: EventBrand
  children: ReactNode
  compact?: boolean
}) {
  return (
    <Box
      sx={{
        minHeight: compact ? 420 : '100vh',
        height: compact ? 420 : undefined,
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
      }}
    >
      {/* Brand panel */}
      <Box
        sx={{
          flex: 1,
          color: '#fff',
          background: brandBackground(brand),
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          p: compact ? 3 : { xs: 4, md: 8 },
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Box sx={{ position: 'relative', maxWidth: 460 }}>
          {brand.logoUrl ? (
            <Box
              component="img"
              src={resolveAssetUrl(brand.logoUrl)}
              alt={brand.name ?? ''}
              sx={{ maxHeight: compact ? 40 : 56, mb: 2, filter: 'brightness(0) invert(1)' }}
            />
          ) : (
            <Typography variant="overline" sx={{ opacity: 0.85, fontWeight: 700 }}>
              Vericast Events
            </Typography>
          )}
          <Typography
            variant={compact ? 'h5' : 'h3'}
            fontWeight={700}
            sx={{ mt: 1, lineHeight: 1.1 }}
          >
            {brand.name || 'Your event'}
          </Typography>
          {brand.tagline && (
            <Typography
              variant={compact ? 'body1' : 'h6'}
              sx={{ mt: 2, fontWeight: 400, opacity: 0.92 }}
            >
              {brand.tagline}
            </Typography>
          )}
        </Box>
      </Box>

      {/* Content panel */}
      <Box
        sx={{
          flex: { xs: 'unset', md: '0 0 460px' },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: compact ? 3 : { xs: 4, md: 6 },
          bgcolor: 'background.paper',
        }}
      >
        <Container maxWidth="xs" disableGutters>
          {!compact && (
            <Box sx={{ mb: 4 }}>
              <Logo />
            </Box>
          )}
          {children}
        </Container>
      </Box>
    </Box>
  )
}
